import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuditorFramework, useAuditorControls } from '@/api/hooks/useAuditorAccess';

const STATUS_COLORS: Record<string, string> = {
  implemented:         'bg-green-100 text-green-800',
  in_progress:         'bg-blue-100 text-blue-800',
  assigned:            'bg-yellow-100 text-yellow-800',
  unassigned:          'bg-gray-100 text-gray-600',
  not_applicable:      'bg-purple-100 text-purple-700',
  compensating_control:'bg-orange-100 text-orange-700',
  submitted:           'bg-cyan-100 text-cyan-800',
  exceptions_only:     'bg-red-100 text-red-700',
};

export function AuditorFrameworkDetailPage() {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data: framework, isLoading: fwLoading } = useAuditorFramework(frameworkId!);
  const { data: controlsData, isLoading: ctrlLoading } = useAuditorControls(frameworkId!, {
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
  });

  const controls = controlsData?.data ?? [];
  const categories = [...new Set(controls.map((c) => c.category).filter(Boolean))].sort();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Link to="/auditor/frameworks" className="text-text-tertiary hover:text-text-primary">
          <ChevronLeft className="h-4 w-4" />
        </Link>
        {fwLoading ? (
          <Skeleton className="h-6 w-48" />
        ) : (
          <h1 className="text-xl font-semibold text-text-primary">{framework?.displayName}</h1>
        )}
      </div>

      {framework && (
        <Card>
          <CardContent className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
            <div>
              <p className="text-xs text-text-tertiary">Total Controls</p>
              <p className="text-lg font-bold text-text-primary">{framework.totalControls}</p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Implemented</p>
              <p className="text-lg font-bold text-green-600">{framework.implementedCount ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">In Progress</p>
              <p className="text-lg font-bold text-blue-500">{framework.inProgressCount ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Readiness</p>
              <p className="text-lg font-bold text-text-primary">
                {framework.readinessPct != null ? `${framework.readinessPct}%` : '—'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm">Controls</CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-7 rounded border border-border-subtle bg-bg-surface px-2 text-xs text-text-primary"
            >
              <option value="">All categories</option>
              {categories.map((c) => <option key={c as string} value={c as string}>{c}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-7 rounded border border-border-subtle bg-bg-surface px-2 text-xs text-text-primary"
            >
              <option value="">All statuses</option>
              <option value="implemented">Implemented</option>
              <option value="in_progress">In Progress</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
              <option value="not_applicable">N/A</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {ctrlLoading ? (
            <div className="space-y-2 p-4">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : controls.length === 0 ? (
            <p className="py-8 text-center text-sm text-text-tertiary">No controls found</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border-subtle">
                <tr className="text-left text-xs text-text-tertiary">
                  <th className="px-4 py-2 font-medium">Control ID</th>
                  <th className="px-4 py-2 font-medium">Title</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {controls.map((ctrl) => (
                  <tr key={ctrl.id} className="border-b border-border-subtle hover:bg-bg-surface-2">
                    <td className="px-4 py-2 font-mono text-xs font-medium text-text-primary">
                      {ctrl.controlId}
                    </td>
                    <td className="px-4 py-2">
                      <Link
                        to={`/auditor/frameworks/${frameworkId}/controls/${ctrl.id}`}
                        className="text-text-primary hover:underline"
                      >
                        {ctrl.title}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      {ctrl.assignmentStatus ? (
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[ctrl.assignmentStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                          {ctrl.assignmentStatus.replace(/_/g, ' ')}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2 text-text-secondary">{ctrl.evidenceCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
